import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import CustomPrintModalBalanceGeneral from '../UI/CustomPrintModalBalanceGeneral';

const BalanceGeneralModal = ({ show, handleClose, cuentasMap, fechaInicio, fechaFinal }) => {
  const [numLevels, setNumLevels] = useState(3);
  const [customHeader, setCustomHeader] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState(null);

  const isValidLevel = numLevels >= 3 && numLevels <= 10;

  const filtrarCuentasPorNivel = (cuentas, numLevels) => {
    const resultado = [];
  
    cuentas.forEach((cuenta) => {
      const nivel = Math.floor(cuenta.codigo_cuenta.length / 2);
      if (nivel <= numLevels && typeof cuenta.codigo_cuenta === "string" && cuenta.codigo_cuenta.trim() !== "") {
        resultado.push(cuenta);
      }
  
      if (cuenta.subcuentas && cuenta.subcuentas.length > 0) {
        const subcuentasFiltradas = filtrarCuentasPorNivel(cuenta.subcuentas, numLevels);
        resultado.push(...subcuentasFiltradas);
      }
    });
  
    return resultado;
  };

  const prepareBalanceGeneralContent = () => {
    if (!cuentasMap || cuentasMap.length === 0) {
      Swal.fire('Error', 'No hay datos de cuentas disponibles', 'error');
      return;
    }

    // Verificar que las cuentas principales existan
    const hasMainAccounts = 
      cuentasMap.some(c => c.codigo_cuenta === '01') &&
      cuentasMap.some(c => c.codigo_cuenta === '02') &&
      cuentasMap.some(c => c.codigo_cuenta === '03');

    if (!hasMainAccounts) {
      Swal.fire('Error', 'Faltan cuentas principales en los datos', 'error');
      return;
    }

    const filteredAccounts = filtrarCuentasPorNivel(cuentasMap, numLevels);
      
    if (filteredAccounts.length === 0) {
      Swal.fire('Error', 'No se encontraron cuentas dentro del nivel seleccionado', 'error');
      return;
    }

    // Clasificar cuentas
    const activos = filteredAccounts.filter((cuenta) => cuenta.codigo_cuenta.startsWith("01"));
    const pasivos = filteredAccounts.filter((cuenta) => cuenta.codigo_cuenta.startsWith("02"));
    const patrimonio = filteredAccounts.filter((cuenta) => cuenta.codigo_cuenta.startsWith("03"));

    // Encontrar cuentas principales con valores por defecto
    const cuentaPrincipalActivos = activos.find(c => c.codigo_cuenta === "01") || { saldo: 0 };
    const cuentaPrincipalPasivos = pasivos.find(c => c.codigo_cuenta === "02") || { saldo: 0 };
    const cuentaPrincipalPatrimonio = patrimonio.find(c => c.codigo_cuenta === "03") || { saldo: 0 };

    // Calcular totales
    const totalActivos = Number(cuentaPrincipalActivos.saldo) || 0;
    const totalPasivos = Number(cuentaPrincipalPasivos.saldo) || 0;
    const totalPatrimonio = Number(cuentaPrincipalPatrimonio.saldo) || 0;
    const totalPasivosPatrimonio = totalPasivos + totalPatrimonio;

    // Preparar contenido para impresión
    const content = {
      activos: activos
        .filter((cuenta) => Math.floor(cuenta.codigo_cuenta.length / 2) <= numLevels)
        .map((cuenta) => ({
          codigo_cuenta: cuenta.codigo_cuenta,
          nombre: cuenta.nombre_cuenta || "Sin Nombre",
          valor: Number(cuenta.saldo) || 0,
        })),
      pasivosYpatrimonio: [
        ...pasivos
          .filter((cuenta) => Math.floor(cuenta.codigo_cuenta.length / 2) <= numLevels)
          .map((cuenta) => ({
            codigo_cuenta: cuenta.codigo_cuenta,
            nombre: cuenta.nombre_cuenta || "Sin Nombre",
            valor: Number(cuenta.saldo) || 0,
          })),
        ...patrimonio
          .filter((cuenta) => Math.floor(cuenta.codigo_cuenta.length / 2) <= numLevels)
          .map((cuenta) => ({
            codigo_cuenta: cuenta.codigo_cuenta,
            nombre: cuenta.nombre_cuenta || "Sin Nombre",
            valor: Number(cuenta.saldo) || 0,
          })),
      ],
      totalActivos,
      totalPasivosPatrimonio,
    };

    setPrintContent(content);
  };

  const handlePrint = () => {
    if (isValidLevel) {
      prepareBalanceGeneralContent();
      setShowPrintModal(true);
    } else {
      Swal.fire('Error', 'El nivel seleccionado no es válido', 'error');
    }
  };

  const handleClosePrintModal = () => {
    setShowPrintModal(false);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Configuración del Balance General</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            El Balance General muestra el estado financiero de la entidad,
            incluyendo sus activos, pasivos y patrimonio. Seleccione el número
            de niveles a mostrar y personalice el encabezado.
          </p>
          <div className="mb-3">
            <label htmlFor="numLevels">Número de niveles (2-10):</label>
            <input
              type="number"
              id="numLevels"
              value={numLevels}
              onChange={(e) => setNumLevels(Number(e.target.value))}
              min="2"
              max="10"
              className="form-control"
            />
            {!isValidLevel && (
              <small className="text-danger">
                Por favor, ingrese un nivel entre 2 y 10.
              </small>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="customHeader">Texto de encabezado:</label>
            <input
              type="text"
              id="customHeader"
              value={customHeader}
              onChange={(e) => setCustomHeader(e.target.value)}
              className="form-control"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Volver
          </Button>
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={!isValidLevel}
          >
            Imprimir
          </Button>
        </Modal.Footer>
      </Modal>

      {printContent && (
        <CustomPrintModalBalanceGeneral
          show={showPrintModal}
          handleClose={handleClosePrintModal}
          title1={(customHeader && `${customHeader}\nDEL ${fechaInicio} AL ${fechaFinal}` )|| `BALANCE GENERAL\nDEL ${fechaInicio} AL ${fechaFinal}`}
          content={printContent}
          copia={false}
        />
      )}
    </>
  );
};

export default BalanceGeneralModal;